import { useQuery } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@optools/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { cn } from '@optools/components/ui/utils';
import { GET_VIEWING_POLICIES } from '@optools/services/operator-gateway/esni/queries';
import { ActionType } from '@optools/services/operator-gateway/types/generated/graphql';
import { Link } from '@tanstack/react-router';
import { CheckIcon, CircleX, PlusCircle } from 'lucide-react';

import { ActionTypeCard } from './action-type-card';

export interface ViewingPoliciesActionTypeCardProps {
  actionType: ActionType;
  disabled?: boolean;
  onRemoveClicked: (actionType: ActionType) => void;
  onViewingPoliciesChanged: (selectedValues: string[]) => void;
  selectedViewingPolicies: string[];
}

export function ViewingPoliciesActionTypeCard({
  actionType,
  disabled,
  onRemoveClicked,
  onViewingPoliciesChanged,
  selectedViewingPolicies,
}: ViewingPoliciesActionTypeCardProps) {
  const { data } = useQuery(GET_VIEWING_POLICIES);

  const viewingPolicyOptions =
    data?.viewingPolicies.map((item) => {
      return { label: item.href, value: item.href };
    }) ?? [];

  const selectedViewingPoliciesSet = new Set(selectedViewingPolicies);

  return (
    <ActionTypeCard actionType={actionType} disabled={disabled} onRemoveClicked={onRemoveClicked}>
      <div className="flex w-full flex-col gap-2">
        <Popover modal={true}>
          <PopoverTrigger asChild>
            <Button
              aria-label="Add Viewing Policy"
              className="border-dashed"
              disabled={disabled}
              variant="outline">
              <PlusCircle />
              Add Viewing Policy
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-full min-w-(--radix-popper-anchor-width) p-0"
            side="top">
            <Command>
              <CommandInput placeholder="Filter viewing policies" />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                {viewingPolicyOptions.map((option) => {
                  const isSelected = selectedViewingPoliciesSet.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      aria-label={option.label}
                      className="cursor-pointer"
                      onSelect={() => {
                        if (isSelected) {
                          selectedViewingPoliciesSet.delete(option.value);
                        } else {
                          selectedViewingPoliciesSet.add(option.value);
                        }
                        onViewingPoliciesChanged(Array.from(selectedViewingPoliciesSet));
                      }}>
                      <div
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible',
                        )}>
                        {isSelected && (
                          <CheckIcon aria-label="Selected" className={cn('h-4 w-4')} />
                        )}
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="flex w-full flex-col gap-2">
          {selectedViewingPolicies.map((item) => {
            const policy = data?.viewingPolicies.find((policy) => policy.href === item);
            return (
              <div
                key={item}
                className="flex items-center justify-between gap-2"
                data-testid={item}>
                {disabled && policy ? (
                  <Button asChild variant="link">
                    <Link
                      from="/"
                      search={{ details: `policy_${policy?.name}` }}
                      to="/esni/policies">
                      <span className="truncate text-sm">{item}</span>
                    </Link>
                  </Button>
                ) : (
                  <span className="truncate text-sm">{item}</span>
                )}

                <Button
                  aria-label={`Remove ${item}`}
                  className={cn({ invisible: disabled })}
                  disabled={disabled}
                  size="icon-sm"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    selectedViewingPoliciesSet.delete(item);
                    onViewingPoliciesChanged(Array.from(selectedViewingPoliciesSet));
                  }}>
                  <CircleX />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </ActionTypeCard>
  );
}
